/**
 * Axelar Network API Service
 *
 * Axelar is a cross-chain communication protocol using Cosmos SDK.
 * Uses public LCD (REST) endpoints - no API key required.
 *
 * API Endpoints:
 * - Mainnet: https://lcd-axelar.imperator.co
 * - Alternative: https://axelar-lcd.quickapi.com
 * - RPC: https://axelar-rpc.quickapi.com
 */

import type {
  AxelarAccount,
  AxelarBalance,
  AxelarDelegation,
  AxelarValidator,
  AxelarReward,
  AxelarUnbonding,
  AxelarTransaction,
  AxelarNetworkStats,
  AxelarSupportedChain,
  AxelarBalanceResponse,
  AxelarDelegationResponse,
  AxelarRewardsResponse,
  AxelarValidatorsResponse,
} from '../types/axelar.types';

// API base URLs
const LCD_API = 'https://lcd-axelar.imperator.co';
const AXELARSCAN_API = 'https://api.axelarscan.io';

// AXL token denom
const AXL_DENOM = 'uaxl';
const AXL_DECIMALS = 6;

// Convert uaxl to AXL
function formatAXL(amount: string): string {
  const value = parseInt(amount, 10) / Math.pow(10, AXL_DECIMALS);
  return value.toFixed(6);
}

// Sum balances of a specific denom
function sumBalances(balances: AxelarBalance[], denom: string = AXL_DENOM): string {
  const total = balances
    .filter((b) => b.denom === denom)
    .reduce((sum, b) => sum + parseInt(b.amount, 10), 0);
  return total.toString();
}

class AxelarApiService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = LCD_API;
  }

  /**
   * Validate Axelar address format
   */
  isValidAddress(address: string): boolean {
    return /^axelar1[a-z0-9]{38,}$/.test(address);
  }

  /**
   * Get account balances
   */
  async getBalances(address: string): Promise<AxelarBalance[]> {
    try {
      const response = await fetch(
        `${this.baseUrl}/cosmos/bank/v1beta1/balances/${address}`
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch balances: ${response.status}`);
      }

      const data: AxelarBalanceResponse = await response.json();
      return data.balances || [];
    } catch (error) {
      console.error('[AxelarAPI] Error fetching balances:', error);
      throw error;
    }
  }

  /**
   * Get delegations for an address
   */
  async getDelegations(address: string): Promise<AxelarDelegation[]> {
    try {
      const response = await fetch(
        `${this.baseUrl}/cosmos/staking/v1beta1/delegations/${address}`
      );

      if (!response.ok) {
        if (response.status === 404) {
          return []; // No delegations
        }
        throw new Error(`Failed to fetch delegations: ${response.status}`);
      }

      const data: AxelarDelegationResponse = await response.json();

      return (data.delegation_responses || []).map((d) => ({
        delegatorAddress: d.delegation.delegator_address,
        validatorAddress: d.delegation.validator_address,
        shares: d.delegation.shares,
        balance: d.balance,
      }));
    } catch (error) {
      console.error('[AxelarAPI] Error fetching delegations:', error);
      return [];
    }
  }

  /**
   * Get staking rewards
   */
  async getRewards(address: string): Promise<AxelarReward[]> {
    try {
      const response = await fetch(
        `${this.baseUrl}/cosmos/distribution/v1beta1/delegators/${address}/rewards`
      );

      if (!response.ok) {
        if (response.status === 404) {
          return [];
        }
        throw new Error(`Failed to fetch rewards: ${response.status}`);
      }

      const data: AxelarRewardsResponse = await response.json();

      return (data.rewards || []).map((r) => ({
        validatorAddress: r.validator_address,
        reward: r.reward || [],
      }));
    } catch (error) {
      console.error('[AxelarAPI] Error fetching rewards:', error);
      return [];
    }
  }

  /**
   * Get unbonding delegations
   */
  async getUnbonding(address: string): Promise<AxelarUnbonding[]> {
    try {
      const response = await fetch(
        `${this.baseUrl}/cosmos/staking/v1beta1/delegators/${address}/unbonding_delegations`
      );

      if (!response.ok) {
        if (response.status === 404) {
          return [];
        }
        throw new Error(`Failed to fetch unbonding: ${response.status}`);
      }

      const data = await response.json();

      return (data.unbonding_responses || []).map((u: {
        delegator_address: string;
        validator_address: string;
        entries: {
          creation_height: string;
          completion_time: string;
          initial_balance: string;
          balance: string;
        }[];
      }) => ({
        delegatorAddress: u.delegator_address,
        validatorAddress: u.validator_address,
        entries: u.entries.map((e) => ({
          creationHeight: e.creation_height,
          completionTime: e.completion_time,
          initialBalance: e.initial_balance,
          balance: e.balance,
        })),
      }));
    } catch (error) {
      console.error('[AxelarAPI] Error fetching unbonding:', error);
      return [];
    }
  }

  /**
   * Get all validators
   */
  async getValidators(status: string = 'BOND_STATUS_BONDED'): Promise<AxelarValidator[]> {
    try {
      const response = await fetch(
        `${this.baseUrl}/cosmos/staking/v1beta1/validators?status=${status}&pagination.limit=200`
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch validators: ${response.status}`);
      }

      const data: AxelarValidatorsResponse = await response.json();

      return (data.validators || []).map((v) => ({
        operatorAddress: v.operator_address,
        moniker: v.description.moniker,
        identity: v.description.identity,
        website: v.description.website,
        commission: {
          rate: v.commission.commission_rates.rate,
          maxRate: v.commission.commission_rates.max_rate,
          maxChangeRate: v.commission.commission_rates.max_change_rate,
        },
        tokens: v.tokens,
        delegatorShares: v.delegator_shares,
        status: v.status as AxelarValidator['status'],
        jailed: v.jailed,
      }));
    } catch (error) {
      console.error('[AxelarAPI] Error fetching validators:', error);
      return [];
    }
  }

  /**
   * Get a specific validator
   */
  async getValidator(validatorAddress: string): Promise<AxelarValidator | null> {
    try {
      const response = await fetch(
        `${this.baseUrl}/cosmos/staking/v1beta1/validators/${validatorAddress}`
      );

      if (!response.ok) {
        return null;
      }

      const data = await response.json();
      const v = data.validator;

      return {
        operatorAddress: v.operator_address,
        moniker: v.description.moniker,
        identity: v.description.identity,
        website: v.description.website,
        commission: {
          rate: v.commission.commission_rates.rate,
          maxRate: v.commission.commission_rates.max_rate,
          maxChangeRate: v.commission.commission_rates.max_change_rate,
        },
        tokens: v.tokens,
        delegatorShares: v.delegator_shares,
        status: v.status,
        jailed: v.jailed,
      };
    } catch (error) {
      console.error('[AxelarAPI] Error fetching validator:', error);
      return null;
    }
  }

  /**
   * Get network staking stats
   */
  async getNetworkStats(): Promise<AxelarNetworkStats | null> {
    try {
      const [poolRes, inflationRes, supplyRes, validatorsRes, blockRes] = await Promise.all([
        fetch(`${this.baseUrl}/cosmos/staking/v1beta1/pool`),
        fetch(`${this.baseUrl}/cosmos/mint/v1beta1/inflation`),
        fetch(`${this.baseUrl}/cosmos/bank/v1beta1/supply/by_denom?denom=${AXL_DENOM}`),
        fetch(`${this.baseUrl}/cosmos/staking/v1beta1/validators?status=BOND_STATUS_BONDED&pagination.count_total=true`),
        fetch(`${this.baseUrl}/cosmos/base/tendermint/v1beta1/blocks/latest`),
      ]);

      const pool = poolRes.ok ? await poolRes.json() : null;
      const inflation = inflationRes.ok ? await inflationRes.json() : null;
      const supply = supplyRes.ok ? await supplyRes.json() : null;
      const validators = validatorsRes.ok ? await validatorsRes.json() : null;
      const block = blockRes.ok ? await blockRes.json() : null;

      const bondedTokens = pool?.pool?.bonded_tokens || '0';
      const totalSupply = supply?.amount?.amount || '0';
      const inflationRate = inflation?.inflation || '0';

      // Calculate APR: (inflation * total_supply) / bonded_tokens
      const apr =
        parseFloat(bondedTokens) > 0
          ? (parseFloat(inflationRate) * parseFloat(totalSupply)) / parseFloat(bondedTokens)
          : 0;

      return {
        totalSupply,
        bondedTokens,
        notBondedTokens: pool?.pool?.not_bonded_tokens || '0',
        inflation: inflationRate,
        apr: apr.toFixed(4),
        activeValidators: parseInt(validators?.pagination?.total || '0', 10),
        totalValidators: parseInt(validators?.pagination?.total || '0', 10),
        blockHeight: block?.block?.header?.height || '0',
        blockTime: 6, // ~6 seconds average
        communityPool: [],
      };
    } catch (error) {
      console.error('[AxelarAPI] Error fetching network stats:', error);
      return null;
    }
  }

  /**
   * Get supported chains on Axelar network
   */
  async getSupportedChains(): Promise<AxelarSupportedChain[]> {
    try {
      const response = await fetch(`${AXELARSCAN_API}/chains`);

      if (!response.ok) {
        throw new Error(`Failed to fetch chains: ${response.status}`);
      }

      const data = await response.json();

      return (data || []).map((c: {
        id: string;
        chain_name: string;
        chain_id: string;
        native_token: { symbol: string };
        image?: string;
        explorer?: { url: string };
        gateway_address?: string;
        maintainer_id?: string;
      }) => ({
        id: c.id,
        name: c.chain_name,
        chainId: c.chain_id,
        native_token: c.native_token?.symbol || '',
        image: c.image,
        explorer_url: c.explorer?.url,
        gateway_address: c.gateway_address,
        status: c.maintainer_id ? 'active' : 'inactive',
      }));
    } catch (error) {
      console.error('[AxelarAPI] Error fetching supported chains:', error);
      return [];
    }
  }

  /**
   * Get recent transactions for an address
   */
  async getTransactions(address: string, limit: number = 20): Promise<AxelarTransaction[]> {
    try {
      // Query transactions where address is sender or recipient
      const response = await fetch(
        `${this.baseUrl}/cosmos/tx/v1beta1/txs?events=message.sender='${address}'&pagination.limit=${limit}&order_by=ORDER_BY_DESC`
      );

      if (!response.ok) {
        return [];
      }

      const data = await response.json();

      return (data.tx_responses || []).map((tx: {
        txhash: string;
        height: string;
        timestamp: string;
        tx: {
          body: {
            messages: { '@type': string; from_address?: string; to_address?: string; amount?: AxelarBalance[] }[];
            memo: string;
          };
          auth_info: { fee: { amount: AxelarBalance[] } };
        };
        code: number;
      }) => {
        const msg = tx.tx?.body?.messages?.[0] || {};
        return {
          hash: tx.txhash,
          height: tx.height,
          timestamp: tx.timestamp,
          type: msg['@type']?.split('.').pop() || 'unknown',
          from: msg.from_address || address,
          to: msg.to_address,
          amount: msg.amount,
          fee: tx.tx?.auth_info?.fee?.amount || [],
          memo: tx.tx?.body?.memo,
          success: tx.code === 0,
        };
      });
    } catch (error) {
      console.error('[AxelarAPI] Error fetching transactions:', error);
      return [];
    }
  }

  /**
   * Get full account data
   */
  async getAccount(address: string): Promise<AxelarAccount> {
    const [balances, delegations, rewards, unbonding] = await Promise.all([
      this.getBalances(address),
      this.getDelegations(address),
      this.getRewards(address),
      this.getUnbonding(address),
    ]);

    // Fetch validator info for each delegation
    const delegationsWithValidators = await Promise.all(
      delegations.map(async (d) => {
        const validator = await this.getValidator(d.validatorAddress);
        return { ...d, validator: validator || undefined };
      })
    );

    // Calculate totals
    const totalStaked = delegations.reduce(
      (sum, d) => sum + parseInt(d.balance.amount, 10),
      0
    );

    const totalRewards = rewards.reduce((sum, r) => {
      const axlReward = r.reward.find((b) => b.denom === AXL_DENOM);
      return sum + parseFloat(axlReward?.amount || '0');
    }, 0);

    const totalUnbonding = unbonding.reduce((sum, u) => {
      return (
        sum +
        u.entries.reduce((entrySum, e) => entrySum + parseInt(e.balance, 10), 0)
      );
    }, 0);

    return {
      address,
      balances,
      delegations: delegationsWithValidators,
      rewards,
      unbonding,
      totalStaked: totalStaked.toString(),
      totalRewards: Math.floor(totalRewards).toString(),
      totalUnbonding: totalUnbonding.toString(),
    };
  }
}

// Export singleton
export const axelarApi = new AxelarApiService();

// Export formatting utilities
export { formatAXL, sumBalances, AXL_DENOM, AXL_DECIMALS };
